import React, { useEffect, useMemo, useState } from "react";
import s from "./AddListing.module.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "../../../../utils/Icons";
import SelectDropdown from "./Dropdown/SelectDropdown";
import backend from "../../../../api";
import { uploadMedia } from "../../../../utils/uploadMedia";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";

const text = (v = "") => v;

export default function AddListing() {
  const { user } = useSelector((s) => s.auth);
  const companyId = user?.companyId;
  const userId = user?._id;

  const navigate = useNavigate();
  const { state } = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const initial = useMemo(
    () => ({
      productName: text(state?.listing?.productName || state?.listing?.name),
      description: text(state?.listing?.description || state?.listing?.subtitle),
      usage: text(state?.listing?.usage),
      dimmable: text(state?.listing?.dimmable),
      temperature: text(state?.listing?.temperature),
      beamAngle: text(state?.listing?.beamAngle),
      size: text(state?.listing?.size),
      wattage: text(state?.listing?.wattage),
      color: text(state?.listing?.color),
      price: state?.listing?.price ? String(state?.listing?.price) : "",
      specifications: text(state?.listing?.specifications),
      brand: text(state?.listing?.brand),
      category: text(state?.listing?.category),
      available: state?.listing?.available ?? true,
      files: state?.listing?.files || {}, // prefill file urls if editing
      image: state?.listing?.image || "",
      images: Array.isArray(state?.listing?.images) ? state?.listing?.images : [],
    }),
    [state]
  );

  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);

  // install guide
  const [installGuide, setInstallGuide] = useState(null);
  const [installBusy, setInstallBusy] = useState(false);
  const [installUrl, setInstallUrl] = useState(initial?.files?.installationGuideUrl || "");

  // 3D model + IES photometrics
  const [model3dBusy, setModel3dBusy] = useState(false);
  const [model3dUrl, setModel3dUrl] = useState(initial?.files?.model3dUrl || "");

  const [iesBusy, setIesBusy] = useState(false);
  const [photometricsUrl, setPhotometricsUrl] = useState(initial?.files?.photometricsUrl || "");

  // product images (now upload immediately and show pills)
  const [imageBusy, setImageBusy] = useState(false);
  const [imageUrls, setImageUrls] = useState(initial.images);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };
  const onSelectChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  // Upload handlers
  const handleUploadInstall = async (file) => {
    if (!file) return;
    try {
      setInstallBusy(true);
      const url = await uploadMedia([file]);
      setInstallUrl(url);
      setInstallGuide(null);
    } catch (err) {
      console.warn("install upload error:", err?.response?.data || err.message);
      alert(err?.response?.data?.error || err.message);
    } finally {
      setInstallBusy(false);
    }
  };

  const handleUpload3D = async (file) => {
    if (!file) return;
    try {
      setModel3dBusy(true);
      const url = await uploadMedia([file]);
      setModel3dUrl(url);
    } catch (err) {
      console.warn("3D model upload error:", err?.response?.data || err.message);
      alert(err?.response?.data?.error || err.message);
    } finally {
      setModel3dBusy(false);
    }
  };

  const handleUploadIES = async (file) => {
    if (!file) return;
    try {
      setIesBusy(true);
      const url = await uploadMedia([file]);
      setPhotometricsUrl(url);
    } catch (err) {
      console.warn("IES upload error:", err?.response?.data || err.message);
      alert(err?.response?.data?.error || err.message);
    } finally {
      setIesBusy(false);
    }
  };

  // NEW: images immediate upload with loader + pill list
  const handleUploadImages = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    try {
      setImageBusy(true);
      const newUrls = [];
      for (const f of list) {
        const url = await uploadMedia([f]);
        newUrls.push(url);
      }
      setImageUrls((arr) => [...arr, ...newUrls]);
    } catch (err) {
      console.warn("image upload error:", err?.response?.data || err.message);
      alert(err?.response?.data?.error || err.message);
    } finally {
      setImageBusy(false);
    }
  };

  const remove3D = () => setModel3dUrl("");
  const removeIES = () => setPhotometricsUrl("");
  const removeInstall = () => setInstallUrl("");
  const removeImage = (url) => setImageUrls((arr) => arr.filter((u) => u !== url));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, userId, companyId };

      // files bundle
      payload.files = {
        ...(payload.files || {}),
        installationGuideUrl: installUrl || undefined,
        model3dUrl: model3dUrl || undefined,
        photometricsUrl: photometricsUrl || undefined,
      };

      // images: already uploaded (no re-upload now)
      payload.images = imageUrls;
      payload.image = imageUrls[0] || payload.image || "";

      if (isEdit && id) {
        await backend.put(`/api/listings/${id}`, payload);
      } else {
        await backend.post(`/api/listings`, payload);
      }
      navigate("/listings");
    } catch (err) {
      console.warn("save listing error:", err?.response?.data || err.message);
      alert(err?.response?.data?.error || err.message);
    }
  };

  return (
    <div className={`${s.page} layoutPadding`}>
      <button className={s.backLink} onClick={() => navigate("/listings")}>
        <ArrowLeftIcon height={15} fill="#878787" />
        Back to listings
      </button>

      <div className={s.card}>
        <h2 className={s.title}>{isEdit ? "Edit Listing" : "Add Listing"}</h2>

        <form className={s.form} onSubmit={submit}>
          {/* Product name */}
          <div className={s.field}>
            <label className={s.label}>Product Name</label>
            <input
              className={s.inputField}
              name="productName"
              value={form.productName}
              onChange={onChange}
              placeholder="Type product name"
              required
            />
          </div>

          {/* Description */}
          <div className={s.field}>
            <label className={s.label}>Product Description</label>
            <div className={s.textAreaCon}>
              <textarea
                name="description"
                value={form.description}
                onChange={onChange}
                placeholder="Write short description"
              />
            </div>
          </div>

          {/* Usage / Dimmable */}
          <div className={s.row2}>
            <div className={s.field}>
              <label className={s.label}>Usage</label>
              <SelectDropdown
                options={[
                  { value: "indoor", label: "Indoor" },
                  { value: "outdoor", label: "Outdoor" },
                  { value: "both", label: "Both" },
                ]}
                selected={form.usage}
                onChange={(val) => onSelectChange("usage", val)}
                placeholder="Select usage"
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Dimmable</label>
              <SelectDropdown
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
                selected={form.dimmable}
                onChange={(val) => onSelectChange("dimmable", val)}
                placeholder="Select option"
              />
            </div>
          </div>

          {/* Temperature / Beam Angle */}
          <div className={s.row2}>
            <div className={s.field}>
              <label className={s.label}>Temperature</label>
              <input
                className={s.inputField}
                name="temperature"
                value={form.temperature}
                onChange={onChange}
                placeholder="e.g. 3000K"
              />
            </div>
            <div className={s.field}>
              <label className={s.label}>Beam Angle</label>
              <input
                className={s.inputField}
                name="beamAngle"
                value={form.beamAngle}
                onChange={onChange}
                placeholder="e.g. 24°"
              />
            </div>
          </div>

          {/* Size / Wattage */}
          <div className={s.row2}>
            <div className={s.field}>
              <label className={s.label}>Size</label>
              <input
                className={s.inputField}
                name="size"
                value={form.size}
                onChange={onChange}
                placeholder="e.g. Medium"
              />
            </div>
            <div className={s.field}>
              <label className={s.label}>Wattage</label>
              <input
                className={s.inputField}
                name="wattage"
                value={form.wattage}
                onChange={onChange}
                placeholder="e.g. 95W"
              />
            </div>
          </div>

          {/* Color / Price */}
          <div className={s.row2}>
            <div className={s.field}>
              <label className={s.label}>Color</label>
              <input
                className={s.inputField}
                name="color"
                value={form.color}
                onChange={onChange}
                placeholder="e.g. Black"
              />
            </div>
            <div className={s.field}>
              <label className={s.label}>Price</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={s.inputField}
                name="price"
                value={form.price}
                onChange={onChange}
                placeholder="$"
              />
            </div>
          </div>

          {/* Brand / Category */}
          <div className={s.row2}>
            <div className={s.field}>
              <label className={s.label}>Brand</label>
              <input
                className={s.inputField}
                name="brand"
                value={form.brand}
                onChange={onChange}
                placeholder="e.g. Philips"
              />
            </div>
            <div className={s.field}>
              <label className={s.label}>Category</label>
              <input
                className={s.inputField}
                name="category"
                value={form.category}
                onChange={onChange}
                placeholder="e.g. Ceiling lights"
              />
            </div>
          </div>

          {/* Specifications */}
          <div className={s.field}>
            <label className={s.label}>Specifications</label>
            <div className={s.textAreaCon}>
              <textarea
                name="specifications"
                value={form.specifications}
                onChange={onChange}
                placeholder="Key specs"
              />
            </div>
          </div>

          {/* Uploads */}
          <div className={s.field}>
            <label className={s.label}>
              Upload Installation Guide <span className={s.tag}>Optional</span>
            </label>
            <div className={s.drop}>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setInstallGuide(f);
                  if (f) handleUploadInstall(f);
                }}
                style={{ display: "none" }}
                id="install-file"
              />
              <label htmlFor="install-file" className={s.browse}>Browse</label> or drag and drop
              {installBusy && (
                <span style={{ marginLeft: 10, display: "inline-flex", alignItems: "center" }}>
                  <CircularProgress size={18} />
                  <span style={{ marginLeft: 6 }}>Uploading…</span>
                </span>
              )}
            </div>

            {/* Show uploaded guide */}
            {installUrl ? (
              <div className={s.filePill}>
                <a href={installUrl} target="_blank" rel="noreferrer">Installation Guide</a>
                <button type="button" className={s.pillClose} onClick={removeInstall}>×</button>
              </div>
            ) : null}
          </div>

          {/* 3D Model Upload */}
          <div className={s.field}>
            <label className={s.label}>
              3D Model <span className={s.tag}>Optional</span>
            </label>
            <div className={s.drop}>
              <input
                type="file"
                accept=".obj,.fbx,.gltf,.glb,.3ds,.stl,.zip"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  if (f) handleUpload3D(f);
                }}
                style={{ display: "none" }}
                id="model3d-file"
              />
              <label htmlFor="model3d-file" className={s.browse}>Browse</label> or drag and drop
              {model3dBusy && (
                <span style={{ marginLeft: 10, display: "inline-flex", alignItems: "center" }}>
                  <CircularProgress size={18} />
                  <span style={{ marginLeft: 6 }}>Uploading…</span>
                </span>
              )}
            </div>

            {model3dUrl ? (
              <div className={s.filePill}>
                <a href={model3dUrl} target="_blank" rel="noreferrer">3D Model</a>
                <button type="button" className={s.pillClose} onClick={remove3D}>×</button>
              </div>
            ) : null}
          </div>

          {/* IES Photometrics Upload */}
          <div className={s.field}>
            <label className={s.label}>
              IES Photometrics <span className={s.tag}>Optional</span>
            </label>
            <div className={s.drop}>
              <input
                type="file"
                accept=".ies,.ldt,.zip"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  if (f) handleUploadIES(f);
                }}
                style={{ display: "none" }}
                id="ies-file"
              />
              <label htmlFor="ies-file" className={s.browse}>Browse</label> or drag and drop
              {iesBusy && (
                <span style={{ marginLeft: 10, display: "inline-flex", alignItems: "center" }}>
                  <CircularProgress size={18} />
                  <span style={{ marginLeft: 6 }}>Uploading…</span>
                </span>
              )}
            </div>

            {photometricsUrl ? (
              <div className={s.filePill}>
                <a href={photometricsUrl} target="_blank" rel="noreferrer">IES Photometrics</a>
                <button type="button" className={s.pillClose} onClick={removeIES}>×</button>
              </div>
            ) : null}
          </div>

          {/* Product Images (immediate upload + pills + loader) */}
          <div className={s.field}>
            <label className={s.label}>Upload Product Images</label>
            <div className={s.drop}>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleUploadImages(e.target.files)}
                style={{ display: "none" }}
                id="img-files"
              />
              <label htmlFor="img-files" className={s.browse}>Browse</label> or drag and drop
              {imageBusy && (
                <span style={{ marginLeft: 10, display: "inline-flex", alignItems: "center" }}>
                  <CircularProgress size={18} />
                  <span style={{ marginLeft: 6 }}>Uploading…</span>
                </span>
              )}
            </div>

            {/* Uploaded image pills */}
            {imageUrls?.length ? (
              <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                {imageUrls.map((u) => (
                  <div key={u} className={s.filePill}>
                    {/* small thumb + link */}
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <img
                        src={u}
                        alt=""
                        style={{ width: 26, height: 26, borderRadius: 6, objectFit: "cover" }}
                      />
                      <a href={u} target="_blank" rel="noreferrer">Image</a>
                    </span>
                    <button type="button" className={s.pillClose} onClick={() => removeImage(u)}>×</button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* Actions */}
          <div className={s.actions}>
            <button
              type="button"
              className={`${s.btn} ${s.btnGhost}`}
              onClick={() => navigate("/listings")}
            >
              Back
            </button>
            <button type="submit" className={`${s.btn} ${s.btnPrimary}`} disabled={imageBusy || installBusy || model3dBusy || iesBusy}>
              {isEdit ? "Save Changes" : "Finish"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
